import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthMethods } from './hooks/useAuthMethods';
import { useProfileFetching } from './hooks/useProfileFetching';
import { useProfileUpdating } from './hooks/useProfileUpdating';
import { AuthContextType, Profile } from './types';
import { toast } from 'sonner';

export const useAuthProvider = (): AuthContextType => {
  const [session, setSession] = useState<any>(null);
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const isFetchingProfile = useRef(false);
  const lastProfileFetchId = useRef<string | null>(null);
  const initialSessionCheckComplete = useRef(false);

  // Set up auth state change handler
  useEffect(() => {
    console.log('Setting up auth state change handler...');
    
    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, currentSession) => {
      console.log('Auth state change event:', event);
      
      if (currentSession) {
        console.log('Session updated with user:', currentSession.user?.id);
        setSession(currentSession);
        setUser(currentSession.user);
      } else {
        console.log('Session cleared');
        setSession(null);
        setUser(null);
        setProfile(null);
        lastProfileFetchId.current = null;
      }
    });

    // Initial session check - check once on component mount
    const checkExistingSession = async () => {
      try {
        console.log('Checking for existing session...');
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        
        if (currentSession) {
          console.log('Found existing session for user:', currentSession.user?.id);
          setSession(currentSession);
          setUser(currentSession.user);
        } else {
          console.log('No existing session found');
          setIsLoading(false);
        }
        
        initialSessionCheckComplete.current = true;
      } catch (error) {
        console.error('Error checking session:', error);
        setIsLoading(false);
        initialSessionCheckComplete.current = true;
      }
    };

    checkExistingSession();

    return () => {
      console.log('Cleaning up auth state change subscription');
      subscription.unsubscribe();
    };
  }, []);

  // Fetch profile when user changes
  useEffect(() => {
    if (user && !isFetchingProfile.current) {
      // Prevent duplicate profile fetches for the same user
      if (lastProfileFetchId.current === user.id) {
        console.log('Skipping duplicate profile fetch for user:', user.id);
        return;
      }
      
      console.log('User detected, fetching profile for ID:', user.id);
      setIsProfileLoading(true);
      isFetchingProfile.current = true;
      lastProfileFetchId.current = user.id;
      
      // Use setTimeout to avoid Supabase auth deadlocks
      setTimeout(() => {
        fetchUserProfile(user.id)
          .catch(error => {
            console.error('Error fetching profile:', error);
            toast.error('Failed to load user profile');
          })
          .finally(() => {
            isFetchingProfile.current = false;
            setIsProfileLoading(false);
          });
      }, 0);
    } else if (!user && initialSessionCheckComplete.current) {
      console.log('No user, clearing profile...');
      setProfile(null);
      setIsLoading(false);
    }
  }, [user]);

  const { fetchProfile: fetchUserProfile } = useProfileFetching(
    user, 
    (newProfile) => {
      console.log('Profile updated:', newProfile?.id);
      setProfile(newProfile);
    }
  );
  
  const { signUp: supabaseSignUp, signIn: supabaseSignIn, signOut: supabaseSignOut, updateEmail, updatePassword } = useAuthMethods(setIsLoading);
  
  // Get update functions with proper type handling
  const { 
    updateProfile: updateUserProfile, 
    updateDemographics: updateUserDemographics 
  } = useProfileUpdating(user?.id, profile, setProfile);

  // Wrapper functions with void return types to match AuthContextType
  const signUp = async (email: string, password: string, userData: Partial<Profile> = {}): Promise<void> => {
    await supabaseSignUp(email, password, userData);
  };

  const signIn = async (email: string, password: string): Promise<void> => {
    await supabaseSignIn(email, password);
  };

  const signOut = async (): Promise<void> => {
    await supabaseSignOut();
  };

  // Wrapper functions to match the expected return type in AuthContextType
  const updateProfile = async (data: Partial<Profile>): Promise<void> => {
    await updateUserProfile(data);
  };

  const updateDemographics = async (data: any): Promise<void> => {
    await updateUserDemographics(data);
  };

  // Update loading state when profile is fetched
  useEffect(() => {
    if (initialSessionCheckComplete.current && 
        ((user && !isProfileLoading && profile !== undefined) || !user)) {
      console.log('Auth provider ready: user and profile state synchronized');
      setIsLoading(false);
    }
  }, [user, profile, isProfileLoading]);
  
  // Compute isAuthenticated based on session existence
  const isAuthenticated = !!session && !!user;

  return {
    session,
    user,
    profile,
    isLoading,
    isAuthenticated,
    signUp,
    signIn,
    signOut,
    updateProfile,
    updateDemographics,
    getUserRole: () => profile?.role || 'public'
  };
};
